import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "_metronic/_helpers";
import { colors } from "app/popleads/components/ui-kit/colors";
import Button from "app/popleads/components/ui-kit/Button";
import TextAreaInput from "app/popleads/components/ui-kit/TextAreaInput";
import FeatherIcon from "feather-icons-react";
import Avatar from "app/popleads/components/ui-kit/Avatar";
import { BodyStrong, Caption, FieldLabel } from "app/popleads/components/ui-kit/Text";
import ChatItem from "app/popleads/components/Chat/AssistantChatItem";
import ChatSkeleton from "app/popleads/components/Chat/AssistantChatSkeleton";
import AssistantEmptyState from "app/popleads/components/Chat/AssistantEmptyState";
import AttachmentChip from "app/popleads/components/Chat/AttachmentChip";
import { conversationMatches } from "app/popleads/components/Chat/assistantHistory";
import { ASSISTANT_ORB, ASSISTANT_SPARK } from "app/popleads/components/Chat/assistantIcons";
/* The CRM window's own classes for the parts drawn the same in both shells — the archive head and
   actions, and the empty block — so the two cannot drift apart. */
import drawerStyles from "app/popleads/components/Chat/AssistantDrawer.module.scss";
import useAssistantThread from "app/popleads/components/Chat/useAssistantThread";
import { ASSISTANT_BUSY } from "app/popleads/components/Chat/assistantErrorToast";
import { toast } from "react-toastify";
import { AssistantFailure, AssistantFailureText, ai } from "./extensionAssistantTheme";

/**
 * `ChatItem` and `ChatSkeleton` are shared with the CRM's command palette and read their accent
 * from `--assistant-accent`/`-hover`/`-tint`, falling back to `leoron` when nothing sets them.
 * Declaring the same `leoron` values here, on the ancestor everything else in this panel renders
 * inside, is what keeps a shared answer bubble reading identically in both shells rather than
 * silently falling back to whichever one happens not to set the variable — see
 * `extensionAssistantTheme.js` for the full history of what `ai.accent` used to be.
 */
const Page = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    /* The CRM window's typeface, not the extension's Gilroy: the same assistant reads the same in
       both shells. Everything in the panel inherits it, the shared answer blocks included. */
    font-family: ${ai.font};
    /* One white surface, as the CRM window is (.palette), over the popup's own tinted frame. */
    background-color: ${colors.white};
    --assistant-accent: ${ai.accent};
    --assistant-accent-hover: ${ai.accentStrong};
    --assistant-accent-tint: ${ai.accentTint};
`;

/* Covers the whole panel rather than just the composer: a file dragged in from the LinkedIn tab is
   dropped wherever the cursor happens to be, not necessarily over the footer. `pointer-events: none`
   because this sits on top of everything and must not be what the drop itself lands on — the
   listeners are on `Page`, which the overlay would otherwise shadow. */
const DropOverlay = styled.div`
    position: absolute;
    inset: 6px;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed ${ai.accent};
    border-radius: 10px;
    /* The accent, l3rnTech[900], at low alpha — see extensionAssistantTheme.js. */
    background-color: rgba(43, 43, 117, 0.05);
    pointer-events: none;
`;

const DropOverlayText = styled.span`
    padding: 8px 14px;
    border-radius: 20px;
    background-color: ${colors.white};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
    font-size: 13px;
    font-weight: 600;
    color: ${ai.accent};
`;

/* The archive and the thread side by side, with the composer below both — the same arrangement the
   CRM palette uses, so the two shells read as one assistant. */
const Main = styled.div`
    display: flex;
    flex: 1;
    min-height: 0;
`;

const Body = styled.div`
    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    gap: 4px;
    box-sizing: border-box;
    padding: 12px;
    overflow-x: hidden;
    overflow-y: auto;

    /* "More"'s list in one column, as the CRM window draws it on a phone: the thread beside the
       archive is phone-narrow, and two columns cut every suggestion short. */
    .${drawerStyles.suggestionList} {
        grid-template-columns: minmax(0, 1fr);
    }
`;

/* AssistantDrawer.module.scss's .thread: answers without a box need the room between turns. */
const Thread = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
    width: 100%;
    padding: 6px 4px 4px 4px;
`;

/* The CRM window's archive labels (.archiveTitle, .archiveGroupLabel): uppercase monospace. */
const ArchiveTitle = styled(FieldLabel)`
    ${ai.mono};
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: ${ai.label};
`;

const GroupLabel = styled(FieldLabel)`
    ${ai.mono};
    display: block;
    padding: 6px 4px 4px 4px;
    font-size: 11px;
    font-weight: 400;
    color: ${ai.label};
`;

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex-shrink: 0;
    padding: 10px 12px;
    border-top: 1px solid ${colors.neutral[100]};
    background-color: ${colors.white};
`;

const Composer = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 6px;
    box-sizing: border-box;
    width: 100%;
    padding: 6px 6px 6px 10px;
    border: 1px solid ${colors.neutral[200]};
    border-radius: 12px;
    background-color: #fff;

    &:focus-within {
        border-color: ${ai.accent};
        box-shadow: 0 0 0 3px rgba(43, 43, 117, 0.12);
    }

    /* The send action only — matches AssistantDrawer.module.scss's .composer button exactly,
       rather than the kit's own neutral[900]/[200] defaults for a plain variant="neutral" fill,
       which are close to but not the same hex as this file's own ai.accent / colors.neutral[200]
       line colour. Scoped to :last-child, not a bare "> button", because the attach button
       beside it is also a direct-child <button> and must keep its own transparent fill — see
       AttachButton below. */
    > button:last-child {
        background-color: ${ai.accent};

        &:not(:disabled):hover {
            background-color: ${ai.accentStrong};
            box-shadow: 0 4px 8px rgba(30, 30, 79, 0.24);
        }

        &:disabled {
            background-color: ${colors.neutral[200]};
        }

        svg {
            color: #fff;
        }
    }

    /* The kit's TextAreaInput carries its own box; inside the composer the composer is the box. */
    textarea {
        /* Block, and this is not cosmetic: a textarea is inline-block by default, so its bottom sat
           on the wrapper's baseline and the wrapper kept the descender space below it. Bottom-
           aligned in the composer, that empty strip pushed the text about 5px above the send
           button's centre — the placeholder read as sitting high in the box, which it was. */
        display: block;
        min-height: 32px;
        max-height: 116px;
        padding: 6px 0;
        border: 0;
        background-color: transparent;
        resize: none;

        &:focus {
            /* The padding is repeated here, and it has to be. The kit's TextAreaInput re-pads
               itself to 10px 14px on focus, and that rule (its own class + a pseudo-class) outweighs
               the unfocused rule above (this class + an element), so focusing the composer used to
               shove the placeholder 14px right and 4px down. Specificity decides padding here, not
               source order — see the note in CLAUDE.md. If the value above changes, change it here. */
            padding: 6px 0;
            border: 0;
            box-shadow: none;
        }
    }
`;

/* Same row as Send, same reach — the easiest a control can be to find is the one already under the
   cursor when you're about to send something. Matches AssistantDrawer.module.scss's own
   .attachButton: a circular, transparent-fill icon button whose icon stays muted at rest (never
   the accent — attach is not the action that reaches the assistant) and whose only hover feedback
   is a light background tint, not a colour change. */
const AttachButton = styled(Button)`
    flex: 0 0 auto;
    border-radius: 50%;
    background-color: transparent;

    &:not(:disabled):hover {
        background-color: ${colors.neutral[100]};
        box-shadow: none;
    }

    svg {
        color: ${ai.muted};
    }
`;

const HiddenFileInput = styled.input`
    display: none;
`;

/* A row, not a stack: each chip is sized to its own name rather than the full width, so a handful
   of files sit on one line instead of taking one each. */
const PendingAttachments = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 8px;
`;

const ComposerField = styled.div`
    flex: 1;
    min-width: 0;
`;

/* Inlined, not an img: the spark is drawn on currentColor, which an img resolves against the SVG
   document and would render black. */
const ComposerMark = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 20px;
    height: 32px;
    color: ${ai.accent};

    svg {
        width: 16px;
        height: 16px;
    }
`;

const Hint = styled(Caption)`
    display: block;
    min-width: 0;
    color: ${ai.muted};
`;

/**
 * The hint and New chat on one line, under the composer.
 *
 * They share a line rather than taking one each because both are about the same thing — what to do
 * next — and the drawer's spare height belongs to the thread. It also puts starting over at the end
 * of the conversation rather than above it, which is where someone is looking when they decide to.
 */
const FooterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    /* New chat — matches AssistantDrawer.module.scss's .footerMeta button rather than the kit's
       own neutral[900]/[950] borderless defaults, which are close to but not the same hex as
       this file's ai.accent / ai.accentStrong. */
    > button {
        color: ${ai.accent};

        &:not(:disabled):hover {
            color: ${ai.accentStrong};
        }
    }
`;

/**
 * Past conversations, as a fixed column beside the thread — no toggle, no slide, no sheet over the
 * conversation. It was a sheet first, then a collapsible column, and both were answers to a question
 * this panel does not have: at 662px there is room for an archive and a thread side by side, so
 * there is nothing to trade and nothing to decide. A control that only ever moves the layout away
 * from the one arrangement that fits is a control worth removing.
 *
 * 216px, narrower than the CRM window's 260: just wide enough for the assistant's name in the head
 * without cutting it short, so the thread keeps the rest of a 662px panel.
 */
const Archive = styled.aside`
    flex: 0 0 216px;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    /* The CRM window's archive: white, with its hairline ($assistant-line) to the thread. */
    border-right: 1px solid ${colors.neutral[200]};
    background-color: ${colors.white};
`;

const ArchiveHead = styled.header`
    flex-shrink: 0;
    padding: 10px 12px 6px 12px;
`;

const ArchiveList = styled.div`
    flex: 1;
    min-height: 0;
    padding: 0 8px 12px 8px;
    overflow-y: auto;
`;

const ArchiveRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    border-radius: 8px;
    background-color: ${({ $active }) => ($active ? ai.accentTint : "transparent")};

    > button:first-child {
        flex: 1;
        min-width: 0;
        height: 34px;
        justify-content: flex-start;

        /* Pre-existing, and the actual cause of the trash icon overlapping the title: the kit
           wraps a button's label in its own span (ButtonText), white-space: nowrap and no
           min-width: 0 of its own. As a flex item that span refuses to shrink below the title's
           full text width regardless of what min-width: 0 says two levels up, so the button
           never actually shrank — it just grew past the row and sat on top of its neighbour.
           ArchiveRowTitle's own overflow: hidden / text-overflow: ellipsis had nothing to clip
           against until this span was told it was allowed to be smaller than its content. */
        > span {
            min-width: 0;
        }

        /* Matches AssistantDrawer.module.scss's .archiveRow button:first-child hover. The title's
           own colour is already pinned on ArchiveRowTitle regardless of this, but the background
           tint is not — without it, hovering an archive row gave no feedback at all until it also
           happened to be $active. */
        &:not(:disabled):hover {
            text-decoration: none;
            background-color: ${ai.accentTint};
        }
    }

    /* Belt and suspenders alongside the fix above: even with the title correctly shrinking now,
       the delete button gets no flex-shrink rule of its own, so it defaults to shrinkable too —
       flex-shrink: 0 makes the title the only thing in this row ever allowed to give up width. */
    > button:last-child {
        flex-shrink: 0;

        /* Matches AssistantDrawer.module.scss's .archiveDelete: $tertiary-color at rest, not the
           kit's own neutral[900] borderless default, and $error-color on hover rather than the
           kit's own neutral[950] — the icon needs to read as a warning under the pointer, not
           just as a slightly darker delete icon. color, not fill: the kit's feather icons draw
           with stroke="currentColor", so a fill override is silently ignored. */
        svg {
            color: ${ai.muted};
        }

        &:not(:disabled):hover svg {
            color: #b93d48;
        }
    }
`;

/* Pinned to `ink` (or, for the loaded conversation, `accent` — the ink and the indigo, the same
   split `AssistantDrawer.module.scss` draws between `$assistant-ink` and `$assistant-accent`)
   rather than left to the button's own colouring — explicitly, on the child span, so it wins
   regardless of the button's variant or hover state. */
const ArchiveRowTitle = styled.span`
    display: block;
    min-width: 0;
    overflow: hidden;
    color: ${({ $active }) => ($active ? ai.accent : ai.ink)};
    font-weight: 400;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/**
 * The AI Assistant, in the extension.
 *
 * The same assistant as the CRM's command palette — same thread, same archive, same commands,
 * because both shells call `useAssistantThread`, and the same three regions in the same order:
 * archive beside thread, composer under both. What differs is the frame: the CRM gets a portalled
 * full-screen palette, this gets the extension's drawer.
 *
 * **Where this renders:** in the extension's own sliding popup, at `POPUP_CONTENTS.ASSISTANT` and
 * 662px wide, opened by the orb over the panel (`ExtensionAssistantLauncher`). Its own drawer, not a
 * tab inside `ExtensionAIAssistantPage`: that panel is CAIN, the nightly LinkedIn agent, and the two
 * share no state and no code. They shared a panel for one release only because the 300px header row
 * had no space for a second trigger, which a floating orb does not need.
 *
 * Consequences worth knowing: the popup supplies the chrome — the "AI Assistant" title, the close
 * button, and the width posted to the host page — so this owns neither a title nor a close control,
 * and New chat sits under the composer instead. Cmd/Ctrl+K and Escape belong to the launcher, since
 * they open and close the drawer rather than acting inside it.
 */
const ExtensionAssistantPage = ({ isOpen = true, pendingError, onPendingErrorTaken, onTitleChange }) => {
    const {
        assistantName,
        user,
        messages,
        isLoading,
        failedQuestion,
        isBrowsing,
        retry,
        messageInput,
        setMessageInput,
        canSend,
        sendMessage,
        attachments,
        addAttachments,
        removeAttachment,
        commands,
        moreCommands,
        showCommands,
        runCommand,
        conversationGroups,
        conversationId,
        conversationTitle,
        refreshConversations,
        openConversation,
        removeConversation,
        startNewChat,
        approvingId,
        respondToApproval,
        blockApproval,
        failApproval,
        appendLocalTurn,
        askAboutError,
    } = useAssistantThread();

    /* An error handed over by a toast's "Ask the assistant" (see ExtensionHome): asked once, on a
       new conversation. While an answer is still coming, the user is told so instead. */
    useEffect(() => {
        if (!pendingError) return;
        onPendingErrorTaken();
        if (!askAboutError(pendingError)) toast.info(ASSISTANT_BUSY);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingError]);

    /* Opening the panel starts on a new chat, as the CRM window does; the last one is in the
       archive. The panel stays mounted while the popup is closed, so this keys on the popup opening
       rather than on mount. Not when an error opened it: that asks on its own new conversation (the
       effect above). An answer still on its way is left to finish (startNewChat passes while
       loading). */
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (isOpen && !wasOpenRef.current && !pendingError) startNewChat();
        wasOpenRef.current = isOpen;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);

    /* A counter, not a boolean: the panel has children, and the browser fires a `dragleave` for
       every one of them as the cursor crosses into a child element, each paired with a `dragenter`
       on the way in. Counting nets those out; a boolean would flicker the overlay off between
       children on the way across the panel. */
    const dragCounterRef = useRef(0);
    const [isDragActive, setIsDragActive] = useState(false);

    const handleDragEnter = (e) => {
        e.preventDefault();
        if (!e.dataTransfer.types.includes("Files")) return;
        dragCounterRef.current += 1;
        setIsDragActive(true);
    };

    const handleDragOver = (e) => {
        /* Without this, the browser's default is to reject the drop entirely — dragging over a
           page that never called `preventDefault` on `dragover` shows the "no drop" cursor. */
        e.preventDefault();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) setIsDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        dragCounterRef.current = 0;
        setIsDragActive(false);
        addAttachments(e.dataTransfer.files);
    };

    /* Read on mount rather than once for the life of the extension: the archive is browser storage
       and another tab can have added to it since. */
    useEffect(() => {
        refreshConversations();
    }, [refreshConversations]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [isLoading, messages]);

    /* Grow with the text instead of scrolling inside a fixed box. Again when the composer moves
       between the empty block and the foot: it is a new textarea there, at its default height. */
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 116)}px`;
    }, [messageInput, isBrowsing]);

    /* On opening, and wherever the composer has just moved to. */
    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, [isBrowsing]);

    /* As in the CRM window: the greeting is the thread's first message, but the thread opens on the
       composer, not on a line of the assistant introducing itself. */
    const visibleMessages = messages.filter((message, index) => !(index === 0 && message.role === "assistant"));

    /* The popup's header names the thread, as the CRM window's does: a saved conversation by its
       stored title, an unsaved one by its first question, and an empty one as a new chat. */
    const hasQuestion = messages.some((message) => message.role === "user");
    const savedConversation = conversationGroups
        .flatMap((group) => group.items)
        .find((conversation) => conversation.id === conversationId);
    const threadTitle = hasQuestion
        ? (savedConversation && savedConversation.title) || conversationTitle(messages)
        : "New chat";

    useEffect(() => {
        if (onTitleChange) onTitleChange(threadTitle);
    }, [threadTitle, onTitleChange]);

    /* Search over the conversations column, as in the CRM window: the row turns into a field and
       the list under it filters as you type. */
    const [isSearching, setIsSearching] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const searchRef = useRef(null);
    const searchTerm = searchQuery.trim().toLowerCase();
    const shownGroups = searchTerm
        ? conversationGroups
              .map((group) => ({ ...group, items: group.items.filter((c) => conversationMatches(c, searchTerm)) }))
              .filter((group) => group.items.length > 0)
        : conversationGroups;

    const endSearch = () => {
        setIsSearching(false);
        setSearchQuery("");
    };

    useEffect(() => {
        if (isSearching && searchRef.current) searchRef.current.focus();
    }, [isSearching]);

    /* Enter sends and Shift+Enter breaks the line, and nothing else — the CRM window's composer. With
       the suggestions as chips rather than a list with a highlighted row, an empty Enter has nothing
       to run, and the arrow keys are left to the caret. */
    const onComposerKeyDown = (e) => {
        if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
        e.preventDefault();
        if (canSend) sendMessage();
    };

    /* The composer is one element in two places, as in the CRM window: in the middle of an empty
       thread, with the suggestions under it, and docked at the foot once a question has been asked.
       One element, so there is one textarea and one set of handlers. */
    const composer = (
        <>
            {attachments.length > 0 && (
                <PendingAttachments>
                    {attachments.map((attachment) => (
                        <AttachmentChip
                            key={attachment.id}
                            name={attachment.name}
                            onRemove={() => removeAttachment(attachment.id)}
                        />
                    ))}
                </PendingAttachments>
            )}
            <Composer>
                <ComposerMark aria-hidden="true">
                    <SVG src={toAbsoluteUrl(ASSISTANT_SPARK)} />
                </ComposerMark>
                <ComposerField>
                    <TextAreaInput
                        ref={inputRef}
                        value={messageInput}
                        rows={1}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyDown={onComposerKeyDown}
                        placeholder="Ask anything about your CRM"
                        disabled={isLoading}
                    />
                </ComposerField>
                <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    id="assistant-attach-input"
                    multiple
                    onChange={(e) => {
                        addAttachments(e.target.files);
                        e.target.value = "";
                    }}
                />
                <AttachButton
                    label="Attach files"
                    tooltip="Attach files"
                    icon="paperclip"
                    iconType="feather"
                    variant="neutral"
                    buttonStyle="borderless"
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                />
                <Button
                    label="Send message"
                    tooltip="Send"
                    icon="arrow-right"
                    iconType="feather"
                    variant="neutral"
                    size="small"
                    onClick={() => sendMessage()}
                    disabled={!canSend || isLoading}
                />
            </Composer>
        </>
    );

    /**
     * The archive column. Built here rather than inline so the return below reads as the two regions
     * it actually is: archive beside thread, composer under both. Its head is the CRM window's: the
     * assistant's name and mark, then "New chat" and "Search chats".
     */
    const archive = (
        <Archive aria-label="Past conversations">
            <div className={drawerStyles.archiveHead}>
                <Avatar src={toAbsoluteUrl(ASSISTANT_ORB)} size={28} radius={14} />
                <BodyStrong className={drawerStyles.identityName}>{assistantName}</BodyStrong>
            </div>

            <div className={drawerStyles.archiveActions}>
                <Button
                    variant="neutral"
                    buttonStyle="borderless"
                    size="small"
                    leftIcon="edit"
                    className={drawerStyles.archiveAction}
                    onClick={startNewChat}
                    disabled={isLoading}
                >
                    <span className={drawerStyles.archiveActionLabel}>New chat</span>
                </Button>

                {isSearching ? (
                    <div className={drawerStyles.archiveSearch}>
                        <FeatherIcon icon="search" size={15} />
                        <input
                            ref={searchRef}
                            type="text"
                            value={searchQuery}
                            placeholder="Search chats"
                            aria-label="Search chats"
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                /* Escape leaves the search, not the panel. */
                                if (e.key === "Escape") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    endSearch();
                                }
                            }}
                        />
                        <Button
                            label="Close search"
                            tooltip="Close search"
                            icon="x"
                            variant="neutral"
                            buttonStyle="borderless"
                            size="xsmall"
                            onClick={endSearch}
                        />
                    </div>
                ) : (
                    <Button
                        variant="neutral"
                        buttonStyle="borderless"
                        size="small"
                        leftIcon="search"
                        className={drawerStyles.archiveAction}
                        onClick={() => setIsSearching(true)}
                    >
                        <span className={drawerStyles.archiveActionLabel}>Search chats</span>
                    </Button>
                )}
            </div>

            <ArchiveHead>
                <ArchiveTitle>Chats</ArchiveTitle>
            </ArchiveHead>

            <ArchiveList>
                {conversationGroups.length === 0 && (
                    <Hint>Nothing saved yet. Conversations appear here once the assistant has answered.</Hint>
                )}

                {conversationGroups.length > 0 && shownGroups.length === 0 && (
                    <Hint>No chats match “{searchQuery.trim()}”.</Hint>
                )}

                {shownGroups.map((group) => (
                    <div key={group.label}>
                        <GroupLabel>{group.label}</GroupLabel>
                        {group.items.map((conversation) => (
                            <ArchiveRow key={conversation.id} $active={conversation.id === conversationId}>
                                <Button
                                    variant="neutral"
                                    buttonStyle="borderless"
                                    size="small"
                                    onClick={() => openConversation(conversation)}
                                >
                                    <ArchiveRowTitle $active={conversation.id === conversationId}>
                                        {conversation.title || conversationTitle(conversation.messages)}
                                    </ArchiveRowTitle>
                                </Button>
                                <Button
                                    label={`Delete ${conversation.title}`}
                                    tooltip="Delete"
                                    icon="trash-2"
                                    iconType="feather"
                                    variant="neutral"
                                    buttonStyle="borderless"
                                    size="xsmall"
                                    onClick={(event) => removeConversation(event, conversation)}
                                />
                            </ArchiveRow>
                        ))}
                    </div>
                ))}
            </ArchiveList>
        </Archive>
    );

    return (
        <Page
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <Main>
                {archive}

                {/* Nothing asked yet: the CRM window's empty block in the middle of the thread — a
                    greeting, the composer, and the suggestions as chips under it, "More" included. */}
                <Body className={isBrowsing ? drawerStyles.paletteBodyEmpty : undefined}>
                    {isBrowsing && (
                        <AssistantEmptyState
                            name={user?.name}
                            composer={composer}
                            commands={commands}
                            moreCommands={moreCommands}
                            showCommands={showCommands}
                            runCommand={runCommand}
                            sendMessage={sendMessage}
                        />
                    )}

                    {!isBrowsing && (
                        <Thread>
                            {visibleMessages.map((message) => (
                                <ChatItem
                                    key={message.id}
                                    plain
                                    role={message.role}
                                    content={message.content}
                                    timestamp={message.timestamp}
                                    attachments={message.attachments}
                                    errorContext={message.errorContext}
                                    approval={message.approval}
                                    result={message.result}
                                    isApprovalBusy={approvingId === message.id}
                                    onApprovalDecide={(approve) => respondToApproval(message, approve)}
                                    onApprovalNote={appendLocalTurn}
                                    onApprovalBlock={(reason) => blockApproval(message, reason)}
                                    onApprovalFail={(reason) => failApproval(message, reason)}
                                />
                            ))}
                        </Thread>
                    )}

                    {isLoading && <ChatSkeleton plain />}

                    {failedQuestion && !isLoading && (
                        <AssistantFailure role="alert">
                            <AssistantFailureText>
                                That request did not come back. Your question is still here.
                            </AssistantFailureText>
                            <Button
                                variant="danger"
                                buttonStyle="option"
                                size="xsmall"
                                leftIcon="refresh-cw"
                                iconType="feather"
                                onClick={retry}
                            >
                                Try again
                            </Button>
                        </AssistantFailure>
                    )}

                    <div ref={messagesEndRef} />
                </Body>
            </Main>

            {/* Docked at the foot once a question has been asked; until then the composer is in the
                middle of the empty block above. */}
            {!isBrowsing && (
                <Footer>
                    {composer}

                    <FooterRow>
                        <Hint>Ask a follow-up — the assistant keeps the thread.</Hint>

                        {/* No icon: the two glyphs that fit — a refresh arrow and a plus — say
                            "reload" and "add", and neither is what this does. The words are the
                            affordance. */}
                        <Button variant="neutral" buttonStyle="borderless" size="xsmall" onClick={startNewChat}>
                            New chat
                        </Button>
                    </FooterRow>
                </Footer>
            )}

            {isDragActive && (
                <DropOverlay>
                    <DropOverlayText>Drop to attach</DropOverlayText>
                </DropOverlay>
            )}
        </Page>
    );
};

export default ExtensionAssistantPage;
